import { debounce } from '@theme/utilities';

/**
 * Bande d'images défilant en boucle.
 *
 * Liquid ne rend qu'un seul jeu d'images : c'est ici qu'on le répète jusqu'à couvrir
 * la largeur de l'écran, puis qu'on duplique l'ensemble pour que l'animation puisse
 * décaler d'exactement une moitié et reboucler sans raccord visible.
 *
 * Le serveur ne peut pas faire ce calcul — il ignore la largeur de l'écran — et une
 * marge prise au hasard coûte cher : au-delà d'environ 4000px, le navigateur ne tient
 * plus la piste sur une seule couche et cesse de la déplacer en sous-pixel. Le
 * mouvement est alors arrondi au pixel entier et monte en escalier, ce qui se voit sur
 * un écran à densité 1 et reste invisible sur un écran haute densité.
 *
 * Les copies sont retirées de l'arbre d'accessibilité et du parcours clavier : elles
 * ne sont là que pour remplir l'œil.
 */
class AxImageCarousel extends HTMLElement {
  #piste = null;
  #originaux = [];
  #largeurConteneur = null;

  connectedCallback() {
    this.#piste = this.querySelector('.ax-image-carousel__track');
    if (!this.#piste) return;

    this.#originaux = [...this.#piste.children];
    if (this.#originaux.length === 0) return;

    this.#construire();
    this.#relacherImages();

    window.addEventListener('resize', this.#auRedimensionnement);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#auRedimensionnement);
  }

  #construire() {
    // Repartir des originaux : une reconstruction ne doit pas empiler les copies.
    this.#piste.replaceChildren(...this.#originaux);

    const largeurJeu = this.#piste.scrollWidth;
    if (largeurJeu === 0) return;

    this.#largeurConteneur = this.offsetWidth;
    const repetitions = Math.max(1, Math.ceil(this.#largeurConteneur / largeurJeu));

    const moitie = document.createDocumentFragment();
    for (let i = 0; i < repetitions; i++) {
      for (const item of this.#originaux) {
        moitie.appendChild(i === 0 ? item : this.#copier(item));
      }
    }

    const piste = document.createDocumentFragment();
    piste.appendChild(moitie.cloneNode(true));
    // La seconde moitié est toujours décorative, y compris sa première répétition.
    for (const item of moitie.querySelectorAll('.ax-image-carousel__item')) this.#masquer(item);
    piste.appendChild(moitie);

    this.#piste.replaceChildren(piste);
    this.#reglerDuree(largeurJeu * repetitions);
    this.classList.add('ax-image-carousel--prete');
  }

  /**
   * La vitesse est réglée en pixels par seconde : la durée du cycle en découle, et
   * dépend du nombre de répétitions décidé ici. La calculer côté serveur ferait
   * accélérer la bande sur les écrans assez larges pour en réclamer une de plus.
   */
  #reglerDuree(largeurMoitie) {
    const desktop = window.matchMedia('(min-width: 1200px)').matches;
    const vitesse = parseFloat(
      getComputedStyle(this).getPropertyValue(desktop ? '--ax-ic-speed' : '--ax-ic-speed-mobile')
    );
    if (!vitesse) return;
    this.style.setProperty('--ax-ic-duration', `${(largeurMoitie / vitesse).toFixed(2)}s`);
  }

  #copier(item) {
    const copie = item.cloneNode(true);
    this.#masquer(copie);
    return copie;
  }

  #masquer(item) {
    item.setAttribute('aria-hidden', 'true');
    for (const lien of item.querySelectorAll('a')) lien.setAttribute('tabindex', '-1');
  }

  /**
   * L'observateur du thème ne voit pas ces images : elles vivent dans un conteneur qui
   * rogne, et son `rootMargin` ne travaille que sur l'axe vertical. Elles portent donc
   * `data-lazy-keep`, et on relâche le lot quand la bande approche du viewport.
   */
  #relacherImages() {
    if (!this.hasAttribute('data-ax-ic-lazy')) return;

    const observateur = new IntersectionObserver(
      (entrees) => {
        for (const entree of entrees) {
          if (!entree.isIntersecting) continue;
          document.dispatchEvent(
            new CustomEvent('removeLazyKeep', { detail: { lazyParentElement: this } })
          );
          observateur.disconnect();
        }
      },
      { rootMargin: '200px 0px' }
    );
    observateur.observe(this);
  }

  // Un redimensionnement vertical (barre d'adresse mobile) ne change pas le compte.
  #auRedimensionnement = debounce(() => {
    if (this.offsetWidth === this.#largeurConteneur) return;
    this.#construire();
  }, 250);
}

customElements.get('ax-image-carousel') || customElements.define('ax-image-carousel', AxImageCarousel);
